import { getCdnUrl } from '@desygner/ui-core-cdn-mapping';

export interface Coordinates {
  height: number;
  width: number;
  x: number;
  y: number;
}

export const enum ImageSize {
  Thumb = 'thumb',
  Mobile = 'mobile',
  Tablet = 'tab',
  Web = 'web',
  LargeWeb = 'largeweb',
  Original = 'original',
}

export const IMAGE_SIZES = [
  {
    name: ImageSize.Thumb,
    width: 168,
    height: 168,
  },
  {
    name: ImageSize.Mobile,
    width: 499,
    height: 499,
  },
  {
    name: ImageSize.Tablet,
    width: 800,
    height: 800,
  },
  {
    name: ImageSize.Web,
    width: 1200,
    height: 1200,
  },
  {
    name: ImageSize.LargeWeb,
    width: 2500,
    height: 2500,
  },
  {
    name: ImageSize.Original,
    width: Infinity,
    height: Infinity,
  },
];

export function getImageSizes({
  width,
  height,
  exportMultiplier = 1,
}: {
  width: number;
  height: number;
  exportMultiplier?: number;
}): ImageSize[] {
  const requiredWidth = width * exportMultiplier;
  const requiredHeight = height * exportMultiplier;

  const suitableSizes = IMAGE_SIZES.filter((size) => {
    return size.height >= requiredHeight && size.width >= requiredWidth;
  });

  if (suitableSizes.length === 0) {
    return [ImageSize.Original];
  }

  return suitableSizes
    .sort((a, b) => b.width * b.height - a.width * a.height)
    .map((size) => size.name);
}

export function getImageUrlVersion(
  url: string,
  width: number,
  height: number,
  exportMultiplier = 1
): string[] {
  if (url.includes('/original/')) {
    const sizes = getImageSizes({
      width,
      height,
      exportMultiplier,
    });

    if (sizes.length > 0) {
      return sizes.map((size) => url.replace('/original/', `/${size}/`));
    }
  }

  return [url];
}

export function getImageVersions(url: string, versions: ImageSize[]): string[] {
  return versions.map((version) => url.replace('/original/', `/${version}/`));
}

export function imageToBuffer(value: string): Buffer {
  const data = value.replace(/^data:\w+\/\w+;base64,/, '');

  return Buffer.from(data, 'base64');
}

export function imageBufferToString(buffer: Buffer, contentType: string): string {
  const raw = buffer.toString('base64');

  return `data:${contentType};base64,${raw}`;
}

export function getBoxCoordinates(element: Element): Coordinates | undefined {
  const coordinates = element.getAttribute('data-box-coordinates');

  if (coordinates) {
    const [x, y, width, height] = coordinates.split(' ').map((entry) => Number.parseFloat(entry));

    return {
      x,
      y,
      width,
      height,
    };
  }

  return undefined;
}

export function crop(original: Coordinates, mask: Coordinates): Coordinates {
  return {
    x: mask.x - original.x,
    y: mask.y - original.y,
    width: mask.width,
    height: mask.height,
  };
}

export async function checkImageStatus(
  url: string,
  maxAttempts = 5,
  interval = 500
): Promise<boolean> {
  let attempts = 0;
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  while (attempts < maxAttempts) {
    const response = await fetch(url, { method: 'HEAD' });

    if (response.ok) return true;

    attempts++;
    await sleep(interval);
  }

  return false;
}

export function isExternalImage(url: string, isQa: boolean): boolean {
  const allowedDomains = [
    'static.desygner.com',
    'static.fluer.com',
    'static.qadesygner.xyz',
    'static.builddesygner.xyz',
  ];
  const corsSecureUrl = getCdnUrl(url, isQa);
  const regex = new RegExp(`^(${allowedDomains.join('|')})`);

  return !regex.test(corsSecureUrl);
}

export function flipImage(
  url: string,
  flipHorizontal: boolean,
  flipVertical: boolean
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;

      context.translate(flipHorizontal ? img.width : 0, flipVertical ? img.height : 0);

      context.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1);
      context.drawImage(img, 0, 0);

      resolve(canvas.toDataURL());
    };

    img.onerror = (error) => reject(error);
    img.src = url;
  });
}
